import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./apiClient";

/**
 * Údržba DB — admin API `/admin/api/v1/maintenance` (ROLE_ADMIN). Statistiky,
 * cleanup (HTTP logy / transakční logy / aktivační události / tokeny) s preview+delete, zálohy CRUD,
 * export/import tokenů, full reset. Delegace na MaintenanceService/BackupService/
 * TokenExportService.
 */
const BASE = "/admin/api/v1/maintenance";

export interface MaintenanceStats {
  httpLogsTotal: number;
  httpLogsLast24h: number;
  httpLogsLast7d: number;
  httpLogsLast30d: number;
  transactionLogsTotal: number;
  transactionLogsLast24h: number;
  transactionLogsLast7d: number;
  transactionLogsLast30d: number;
  tokensByState: Record<string, number>;
  tokensTotal: number;
  activationEventsTotal: number;
  activationEventsLast24h: number;
  activationEventsLast7d: number;
  activationEventsLast30d: number;
}

export interface CleanupPreview {
  entity: string;
  recordsToDelete: number;
  olderThan: string | null;
  warningMessage: string | null;
  requiresConfirmation: boolean;
}

export interface CleanupResult {
  success: boolean;
  entity: string;
  deletedRecords: number;
  durationMs: number;
  errorMessage: string | null;
}

export interface TokenCleanupPreview {
  tokensToDelete: number;
  tokensByState: Record<string, number>;
  states: string[];
  olderThanDays: number | null;
  authServerType: string | null;
  warningMessage: string | null;
}

export interface TokenCleanupResult {
  success: boolean;
  deletedTokens: number;
  durationMs: number;
  errorMessage: string | null;
}

export interface BackupInfo {
  filename: string;
  sizeBytes: number;
  sizeFormatted: string;
  createdAt: string;
}

export interface BackupListResult {
  backups: BackupInfo[];
  totalCount: number;
  backupDirectory: string;
}

export interface BackupResult {
  success: boolean;
  backup: BackupInfo | null;
  errorMessage: string | null;
  durationMs: number | null;
}

export interface RestoreResult {
  success: boolean;
  restoredFrom: string;
  errorMessage: string | null;
  warningMessage: string | null;
}

export interface TokenImportPreview {
  valid: boolean;
  exportVersion: string | null;
  totalTokens: number;
  newTokens: number;
  existingTokens: number;
  conflictingTokenIds: string[] | null;
  tokensByState: Record<string, number> | null;
  validationErrors: string[] | null;
  warningMessage: string | null;
}

export interface TokenImportResult {
  success: boolean;
  updatedCount: number;
  skippedCount: number;
  failedCount: number;
  errors: { tokenId: string; errorMessage: string }[] | null;
  errorMessage: string | null;
}

export function useMaintenanceStats() {
  return useQuery({ queryKey: ["maintenance", "stats"], queryFn: () => apiFetch<MaintenanceStats>(`${BASE}/statistics`) });
}

export function useBackups() {
  return useQuery({ queryKey: ["maintenance", "backups"], queryFn: () => apiFetch<BackupListResult>(`${BASE}/backup`) });
}

/** Tabulky s časovou retencí — cesta pod `/maintenance/{entity}` je shodná s názvem. */
export type CleanupEntity = "http-logs" | "transaction-logs" | "activation-events";

/** cleanup preview + delete pro logy a aktivační události. */
export function useLogCleanup(entity: CleanupEntity) {
  const qc = useQueryClient();
  const preview = useMutation({
    mutationFn: (olderThanDays: number) =>
      apiFetch<CleanupPreview>(`${BASE}/${entity}/preview?olderThanDays=${olderThanDays}`),
  });
  const remove = useMutation({
    mutationFn: (olderThanDays: number) =>
      apiFetch<CleanupResult>(`${BASE}/${entity}?olderThanDays=${olderThanDays}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["maintenance", "stats"] }),
  });
  return { preview, remove };
}

interface TokenCleanupArgs {
  states: string[];
  olderThanDays?: number | null;
  authServerType?: string | null;
}

export function useTokenCleanup() {
  const qc = useQueryClient();
  const preview = useMutation({
    mutationFn: ({ states, olderThanDays, authServerType }: TokenCleanupArgs) => {
      const p = new URLSearchParams();
      states.forEach((s) => p.append("states", s));
      if (olderThanDays != null) p.set("olderThanDays", String(olderThanDays));
      if (authServerType) p.set("authServerType", authServerType);
      return apiFetch<TokenCleanupPreview>(`${BASE}/tokens/preview?${p.toString()}`);
    },
  });
  const remove = useMutation({
    mutationFn: ({ states, olderThanDays, authServerType }: TokenCleanupArgs) =>
      apiFetch<TokenCleanupResult>(`${BASE}/tokens`, {
        method: "DELETE",
        json: { states, olderThanDays: olderThanDays ?? null, authServerType: authServerType ?? null },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["maintenance", "stats"] });
      qc.invalidateQueries({ queryKey: ["tokens"] });
    },
  });
  return { preview, remove };
}

export function useCreateBackup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<BackupResult>(`${BASE}/backup`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["maintenance", "backups"] }),
  });
}

export function useRestoreBackup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (filename: string) =>
      apiFetch<RestoreResult>(`${BASE}/backup/${encodeURIComponent(filename)}/restore?confirm=true`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["maintenance"] }),
  });
}

export function useDeleteBackup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (filename: string) =>
      apiFetch<void>(`${BASE}/backup/${encodeURIComponent(filename)}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["maintenance", "backups"] }),
  });
}

export function useFullReset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<CleanupResult>(`${BASE}/reset?confirm=true`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["maintenance"] }),
  });
}

/** Export tokenů → stáhne JSON soubor (raw fetch, API chain). */
export async function exportTokens(body: {
  states: string[] | null;
  authServerType: string | null;
  includePasswords: boolean;
  includeActivationEvents: boolean;
}): Promise<{ ok: boolean; error?: string }> {
  const res = await fetch(`${BASE}/tokens/export`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) return { ok: false, error: `HTTP ${res.status}` };
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `tokens_export_${Date.now()}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { ok: true };
}

async function multipart<T>(path: string, file: File, extra?: Record<string, string>): Promise<T> {
  const form = new FormData();
  form.append("file", file);
  const qs = extra ? "?" + new URLSearchParams(extra).toString() : "";
  const res = await fetch(`${BASE}${path}${qs}`, { method: "POST", credentials: "include", body: form });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

export function usePreviewImport() {
  return useMutation({ mutationFn: (file: File) => multipart<TokenImportPreview>("/tokens/import/preview", file) });
}

export function useImportTokens() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, overwriteExisting }: { file: File; overwriteExisting: boolean }) =>
      multipart<TokenImportResult>("/tokens/import", file, { overwriteExisting: String(overwriteExisting) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["maintenance"] });
      qc.invalidateQueries({ queryKey: ["tokens"] });
    },
  });
}

// ==================== Schedulery ====================

export interface SchedulerSub {
  enabled: boolean;
  retentionDays?: number;
  lastRunAt?: string | null;
  lastRunResult?: string | null;
}

export interface SchedulerStatus {
  autoCleanup: SchedulerSub;
  tokenExpiration: SchedulerSub;
}

/** Stav auto-cleanup + token-expiration schedulerů. */
export function useSchedulers() {
  return useQuery({ queryKey: ["maintenance", "schedulers"], queryFn: () => apiFetch<SchedulerStatus>(`${BASE}/schedulers`) });
}

export function useSetAutoCleanup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { enabled: boolean; retentionDays: number }) =>
      apiFetch<SchedulerStatus>(`${BASE}/schedulers/auto-cleanup`, { method: "PUT", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["maintenance", "schedulers"] }),
  });
}

export function useSetExpirationScheduler() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { enabled: boolean }) =>
      apiFetch<SchedulerStatus>(`${BASE}/schedulers/token-expiration`, { method: "PUT", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["maintenance", "schedulers"] }),
  });
}
